import type { CategoryPrompt } from "../types";

import React from "react";

import {
  Box,
  Layers,
  Percent,
  Ruler,
  Shirt,
  Star,
  Tags,
  Users,
  Wand2,
} from "lucide-react";

export const clothingPrompts: CategoryPrompt[] = [
  {
    icon: <Shirt />,
    title: "Fashion Mistake Listicle Advertorial",
    description:
      "Create a listicle addressing common fashion mistakes, showcasing my current collection as the modern solution with CTAs throughout.",
    prompt: `Build a fashion mistake listicle that identifies outdated or unflattering styling errors and positions your collection as the modern, flattering solution.

**TOPIC:** e.g., "7 Fashion Mistakes Making You Look Outdated" OR "5 Styling Errors Killing Your Confidence" OR "What Not to Wear in 2026"

**COLLECTION FEATURED:** e.g., "Our Spring Collection fixes these issues" OR "Modern essentials that work"

**Reader Type:** Choose one:
- Style-conscious (want to look current)
- Confidence seekers (avoid unflattering looks)
- Wardrobe refreshers (updating closet)

**Tone & Style:**
- Helpful stylist not judgmental critic
- Empowering and confidence-building
- Trend-aware but timeless
- "We've all done this" relatability

**CTA Guidelines:** Each CTA should:
- Reference the solution ("Shop Flattering Fits")
- Use transformation language ("Upgrade Your Look")
- Include style benefit ("Look Polished Instantly")
- Emphasize modernity ("Get the 2026 Update")

**Conversion Tactics Use:**
- Before/after styling comparisons
- Flattery and fit explanations
- Celebrity/influencer validation
- "What to wear instead" direct swaps
- Bundle suggestions for complete looks
- Limited stock on trending pieces

**Key Elements to Include:**
- **Hook:** Relatable fashion frustration (2-3 paragraphs)
- **List Items (5-8 mistakes):** Numbered error → why it doesn't work → your product as solution
- **Mistake Structure Per Item:**
  - Error headline: "Mistake #X: [Specific fashion faux pas]"
  - Why it's unflattering: Fit, proportion, or trend explanation
  - Visual impact: How it affects your look
  - The fix: Your product that solves it
  - Style tip: How to wear the solution
  - Product image: Styled look demonstrating fix
  - Customer quote: Someone who made the switch
- **Product Callouts:** Styling details with "Shop This Look" CTA
- **Comparison Visuals:** Mistake vs. solution side-by-side (2-3 throughout)
- **Styling Guide:** How to incorporate fixes into existing wardrobe
- **Trend Context:** Why this matters now in 2026
- **Complete Look Bundles:** Multiple pieces styled together
- **CTAs:** After mistake #2, after mistake #5, conclusion (5-7 total)
- **FAQ:** 3-4 questions (Fit for my body type? Styling tips? Returns? Sizing?)

**Goal:** Convert style-conscious shoppers into buyers by identifying relatable fashion mistakes and positioning your collection as the modern, flattering solution that instantly upgrades their look and confidence.`,
  },
  {
    icon: <Tags />,
    title: "Style Finder Quiz",
    description:
      "Build a quiz asking about aesthetic preferences and lifestyle, then generate personalized outfit recommendations with 'Add to Cart' bundle option, gated behind email capture.",
    prompt: `Build a style finder quiz that matches customers to personalized outfits based on aesthetic preferences and lifestyle, gated with email capture.

**QUIZ FOCUS:** e.g., "Find Your Style Match" OR "Discover Your Perfect Wardrobe" OR "What's Your Style Personality?"

**COLLECTION RANGE:** e.g., Casual, workwear, evening, seasonal pieces (20-40 items to match from)

**Quiz Taker Type:** Choose one:
- Style seekers (defining their aesthetic)
- Wardrobe builders (need complete looks)
- Overwhelmed shoppers (too many choices)

**Tone & Style:**
- Fun personality quiz energy
- Affirming and confidence-building
- Fashion-forward but accessible
- Personal stylist approach

**Quiz Question Guidelines:** Each question should:
- Use visual answer options (lifestyle images or style examples)
- Feel like personality discovery not interrogation
- Build toward specific style categories
- Include lifestyle context
- 5-7 total questions maximum

**Email Gate Strategy:**
- Place after question #4 (mid-commitment)
- Frame as "Get your personalized style guide"
- Offer incentive: "Unlock looks + 20% stylist discount"
- Single field: email only
- Trust line: "Join 100,000+ styled customers"

**Results Page Elements:**
- **Personalized Headline:** "Your style is [Style Type]: here's your perfect wardrobe"
- **Style Profile Badge:** "You're a [Minimalist Chic/Boho Luxe/etc.]"
- **Curated Outfit Looks:** 3-5 complete styled outfits
- **Individual Pieces:** 8-12 items that match their aesthetic
- **Styling Guide:** How to mix and match their recommendations
- **Product Cards Include:** Image, price, style notes, "Add to Cart" or "Add Look"
- **Match Reasoning:** "You said [answer], so this [style element] fits because..."
- **Bundle Options:** Complete outfits with "Add Look to Cart" one-click
- **Mix & Match Grid:** Visual showing outfit combinations
- **Alternative Options:** Different price points or variations
- **CTAs on Products:** "Shop This Piece" and "Get Complete Look"
- **Retake Option:** "Style evolved? Retake quiz"

**Conversion Tactics Use:**
- Style personality framing ("You're a modern minimalist")
- Complete look bundles (outfit discount)
- Capsule wardrobe building
- Celebrity style comparisons if relevant
- Seasonal appropriateness
- Versatility messaging ("5 outfits from 8 pieces")

**Key Quiz Questions to Include:**
- **Aesthetic Preference:** Minimalist, bohemian, classic, edgy, romantic
- **Lifestyle Context:** Work from home, office, events, casual only
- **Color Palette:** Neutrals, bold, pastels, monochrome, mixed
- **Fit Preference:** Relaxed, tailored, oversized, form-fitting
- **Priority Feature:** Comfort, style, versatility, trend-forward
- **Budget Range:** Essential, balanced, investment pieces
- **Occasion Focus:** Everyday, special events, work, weekend

**Goal:** Convert indecisive shoppers into confident buyers by providing personalized style curation that eliminates overwhelm, with one-click outfit purchasing that simplifies wardrobe building and increases average order value.`,
  },
  {
    icon: <Wand2 />,
    title: "Body Type Fit Guide",
    description:
      "Create a page targeting [specific body type] with detailed measurements, size guides, and customer photos, with fit video demonstrations and style recommendations.",
    prompt: `Build a body-type specific fit guide that helps customers find flattering pieces through detailed sizing, real customer photos, and styling advice.

**BODY TYPE FOCUS:** e.g., "Styling Guide for Petite" OR "Curvy Fit Guide" OR "Tall Women's Sizing" OR "Plus Size Fit Solutions"

**FIT PRIORITIES:** e.g., Length adjustments, proportion balance, comfort zones, flattering cuts

**Shopper Type:** Choose one:
- Fit-frustrated (tired of ill-fitting clothes)
- Size-confused (unsure what will work)
- Body-conscious (want flattering styles)

**Tone & Style:**
- Body-positive and affirming
- Specific and helpful (not generic)
- Empowering through knowledge
- Celebratory of this body type

**CTA Guidelines:** Each CTA should:
- Reference fit confidence ("Made for Your Shape")
- Use flattering language ("Perfectly Proportioned")
- Include fit guarantee ("Free Returns on Sizing")
- Emphasize body type match ("Designed for [Body Type]")

**Conversion Tactics Use:**
- Detailed measurement guides with visuals
- Customer photos from same body type
- Fit videos showing movement and drape
- Size comparison charts
- Styling rules for flattering proportions
- Model measurements displayed

**Key Elements to Include:**
- **Hero Section:** Body type headline, inclusive imagery, "Shop Your Fit" CTA
- **Why This Guide:** Understanding unique fit needs for this body type (2-3 paragraphs)
- **Measurement Guide:** How to measure yourself with diagrams (5-7 key measurements)
- **Size Chart Specific to Body Type:** Detailed sizing with body type considerations
- **Fit Challenges Addressed:** Common issues for this body type + solutions
- **Best Styles for Body Type:** 5-8 silhouettes/cuts that flatter with explanations
- **What to Avoid:** Honest guidance on less flattering styles (builds trust)
- **Customer Photo Gallery:** Real customers with this body type wearing products (8-12 photos)
- **Fit Video Demonstrations:** 2-4 videos showing how pieces fit and move
- **Model Measurements:** Exact stats of models wearing each item
- **Product Recommendations:** Curated collection perfect for this body type
- **Styling Tips:** Proportion tricks and outfit formulas
- **Alterations Guide:** If/when to tailor for perfect fit
- **Fit Guarantee:** Easy returns/exchanges policy
- **Customer Reviews Filtered:** Reviews from similar body types
- **CTAs:** Hero, after size guide, with products, after styling tips (5-6 total)
- **FAQ:** 5-7 questions (Between sizes? Return policy? Model measurements? Stretch? Length options?)

**Goal:** Convert fit-anxious shoppers into confident buyers by providing body-type specific guidance, real customer proof, and detailed measurements that eliminate sizing uncertainty and showcase flattering options tailored to their proportions.`,
  },
  {
    icon: <Ruler />,
    title: "Influencer Collaboration Landing Page",
    description:
      "Build a page for an influencer collaboration featuring their curated selections, styling videos, and exclusive codes, with 'Shop [Creator]'s Picks' sections.",
    prompt: `Build an influencer collaboration landing page that leverages creator credibility and exclusive content to drive conversions through authentic style curation.

**INFLUENCER/CREATOR:** e.g., "@stylebyemma" OR "Jordan Lee Fashion" OR "The Minimal Wardrobe"

**COLLABORATION TYPE:** e.g., "Curated capsule" OR "Signature collection" OR "Style edit"

**Audience Type:** Choose one:
- Influencer's followers (trust their style)
- Fashion enthusiasts (discovering through creator)
- Style seekers (want expert curation)

**Tone & Style:**
- Creator's authentic voice
- Aspirational but relatable
- Community-focused ("join us")
- Exclusive insider access feel

**CTA Guidelines:** Each CTA should:
- Reference creator ("Shop Emma's Edit")
- Use exclusive language ("Get Her Exact Wardrobe")
- Include discount code prominently
- Emphasize limited collaboration

**Conversion Tactics Use:**
- Exclusive creator discount code
- Styling videos with outfit formulas
- Creator's personal favorites explained
- Limited-edition or exclusive items
- Bundle creator's complete looks
- Countdown if time-limited collab

**Key Elements to Include:**
- **Hero Section:** Creator photo/video, collaboration headline, exclusive code display, primary CTA
- **About This Collab:** Creator intro + why they curated these pieces (2-3 paragraphs)
- **Exclusive Discount Code:** Large, copy-able code with savings % and expiration
- **Styling Video(s):** 1-3 embedded videos showing outfit creation and mix-match
- **Creator's Favorites:** Curated selection of 8-15 pieces with creator commentary per item
- **"Shop [Creator]'s Picks" Sections:** Organized by category (everyday, workwear, special)
- **Creator Quote Pulls:** Personal style insights as visual breaks
- **Outfit Formulas:** Creator's go-to combinations with "Shop This Look" bundles
- **Why She Chose This:** Creator explanations for each piece selection
- **Complete Looks:** 3-5 full outfits with "Add Look to Cart" option
- **Behind the Scenes:** Collaboration story or selection process
- **Social Proof:** Creator's follower count, style credibility, past collabs
- **Community Content:** Follower photos recreating creator's looks
- **Limited-Time Element:** If applicable, countdown to collab end
- **CTAs:** Hero, after video, with outfit sections, bundle areas (5-7 total)
- **FAQ:** 4-5 questions (Code expiration? Items sold separately? Restock? Creator earnings disclosure?)

**Goal:** Convert influencer's engaged audience into customers by leveraging authentic creator curation, styling education, and exclusive discount incentives that make followers feel part of an insider style community.`,
  },
  {
    icon: <Star />,
    title: "Customer Styling Gallery",
    description:
      "Create a UGC gallery where customers submit styled photos tagged with size and body type, with filtering by body type, size, or style for similar shoppers.",
    prompt: `Build a customer styling gallery showcasing real people wearing your clothes with detailed size/body info to help shoppers visualize fit and style on similar bodies.

**GALLERY FOCUS:** e.g., "Real Customers, Real Style" OR "See It On Someone Like You" OR "Style Inspiration from Our Community"

**FILTER CATEGORIES:** e.g., By size, body type, height, age, style aesthetic

**Gallery Visitor Type:** Choose one:
- Fit-anxious (need to see on similar body)
- Style inspiration seekers (outfit ideas)
- Size-confused (between sizes, need visual)

**Tone & Style:**
- Inclusive and celebratory
- Body-positive and diverse
- Community-focused celebration
- Authentic over polished

**CTA Guidelines:** Each CTA should:
- Use community language ("Join 50,000+ Styled Customers")
- Reference relatability ("Find Your Style Twin")
- Include easy shopping ("Shop Her Look")
- Encourage participation ("Share Your Style")

**Conversion Tactics Use:**
- Filter by exact size/body type match
- Multiple angles per outfit
- Exact product tags on photos
- "Shop this look" one-click
- Photo submission incentive (discount)
- Height/weight stats for fit context

**Key Elements to Include:**
- **Hero Section:** Headline, diverse photo grid preview, filter options, "Submit Your Photo" CTA
- **Filter Options:** By size (XS-3XL+), body type, height range, age range, style aesthetic, verified purchase
- **Photo Grid Layout:** Hover shows details (name, size, height, body type, products worn)
- **Customer Details Per Photo:** Size worn, height, body type, styling notes, product links
- **Featured Styling Stories (3-5):** Large photos, customer name/stats, full outfit breakdown, multiple angles, "Shop This Look" CTA
- **Size Range Galleries:** Dedicated sections per size with multiple body types
- **Body Type Sections:** Petite, tall, curvy, athletic galleries
- **Product-Specific Galleries:** Filter to see specific item on different bodies
- **"Find Your Style Twin" Tool:** Match by entering your measurements
- **Outfit Details:** Product names, sizes worn, styling tips from customer
- **Multiple Angle Views:** Front, side, back photos when available
- **Photo Submission Section:** "Share your style & get 20% off" with upload
- **Authenticity Indicators:** Verified purchase badges, real customer confirmation
- **Style Tags:** Casual, work, evening, weekend for outfit categorization
- **CTAs:** Top of page, with featured stories, product galleries (5-6 total)
- **FAQ:** 3-4 questions (How to submit? Photo requirements? Privacy? Incentive details?)

**Goal:** Convert fit-anxious shoppers into confident buyers by providing diverse, filterable customer photos with detailed size/body information that lets shoppers see exactly how items look on bodies similar to theirs - eliminating sizing uncertainty and inspiring purchases.`,
  },
  {
    icon: <Users />,
    title: "Clearance Sale Page",
    description:
      "Build a clearance page with urgency messaging, size/color availability indicators, and bulk discounts, with cross-sell recommendations to maximize AOV.",
    prompt: `Build a clearance sale page that drives urgency and maximizes order value through scarcity messaging, real-time availability, and strategic bundling.

**SALE FOCUS:** e.g., "End-of-Season Clearance" OR "Final Stock Sale" OR "Up to 70% Off Select Styles"

**DISCOUNT STRUCTURE:** e.g., "40-70% off" OR "Buy 2 Save 50%, Buy 3 Save 60%"

**Shopper Type:** Choose one:
- Deal hunters (motivated by discounts)
- Bargain wardrobe builders (stocking up)
- Size/color flexible (willing to compromise for price)

**Tone & Style:**
- Urgent but exciting
- Honest about final sale terms
- Value-celebration focused
- Clear about limited availability

**CTA Guidelines:** Each CTA should:
- Emphasize scarcity ("Only X Left")
- Reference savings depth ("Save 70%")
- Use urgency language ("Final Sizes")
- Update with availability changes

**Conversion Tactics Use:**
- Size/color availability grids
- Tiered bulk discounts for multiple items
- "Almost gone" alerts on popular items
- Smart cross-sells (complete the outfit)
- Free shipping threshold progress
- Live stock counters

**Key Elements to Include:**
- **Hero Section:** Sale headline with max discount, "Shop Clearance" CTA
- **Bulk Discount Banner:** "Buy 2: 50% | Buy 3: 60% | Buy 4+: 70%" with progress bar
- **Filter Options:** By discount %, by size available, by category, in-stock only toggle
- **Product Grid:** Discount badge, strikethrough price, sale price, size availability preview
- **Size/Color Indicators:** Visual grid showing available options (green: in stock, gray: sold out)
- **Urgency Tags:** "Low stock," "Final sizes," "Only 2 left in your size"
- **Quick Size Selector:** Hover to see all available sizes without clicking in
- **Cross-Sell Sections:** "Complete the outfit" with other clearance pieces
- **Bulk Discount Progress (Sidebar):** Current cart + items needed for next tier
- **Free Shipping Bar:** Progress to threshold with clearance add-on suggestions
- **Final Sale Terms:** Clear "All sales final - no returns" disclosure
- **Size Guide Link:** Help choosers commit despite no returns
- **Social Proof:** "X people viewing this now" or purchase ticker
- **CTAs:** Hero, on each product, bulk prompts, sticky checkout (always visible)
- **FAQ:** 3-4 questions (Returns? Exchanges? Why clearance? Restock?)

**Goal:** Maximize revenue from final stock by creating urgency through scarcity messaging and real-time availability displays while driving higher AOV through tiered bulk discounts and outfit-completion cross-sells.`,
  },
  {
    icon: <Layers />,
    title: "Flash Sale Campaign Page",
    description:
      "Create a sale page with countdown timers, automatic discount, and bestseller grids filtered to show only sale items with urgency messaging.",
    prompt: `Build a flash sale campaign page that drives immediate purchases through time pressure, automatic discounts, and strategic product showcasing.

**SALE FOCUS:** e.g., "48-Hour Flash Sale" OR "Weekend Only: 40% Off Everything" OR "24-Hour Blitz Sale"

**DISCOUNT TYPE:** e.g., "Automatic 40% off at checkout" OR "Tiered discounts" OR "Select styles 30-60% off"

**Shopper Type:** Choose one:
- Impulse buyers (time pressure converts)
- Deal waiters (waiting for sales to buy)
- FOMO-driven (can't miss limited offers)

**Tone & Style:**
- High-energy and urgent
- Exciting celebration of savings
- Clear about time limitation
- No-pressure but time-conscious

**CTA Guidelines:** Each CTA should:
- Emphasize time scarcity ("Ends in X Hours")
- Reference automatic savings ("40% Off Applied")
- Use urgency language ("Shop Before It Ends")
- Include countdown context

**Conversion Tactics Use:**
- Multiple countdown timer placements
- Auto-applied discount at checkout
- Bestseller social proof
- "Selling fast" live notifications
- Category spotlights (deepest discounts)
- Bundle suggestions for higher spend

**Key Elements to Include:**
- **Hero Section:** Sale headline with discount %, large countdown timer, "Shop Now" CTA
- **Countdown Timer:** Hours:minutes:seconds (large, sticky top bar, updates live)
- **Auto-Discount Banner:** "Discount automatically applied at checkout - no code needed"
- **Sale Bestsellers Grid:** 12-24 top-selling items with discount badges
- **Filter Options:** By category, by discount depth, bestsellers only, price range
- **Product Cards:** Discount %, before/after price, star rating, "Selling fast" tags
- **Category Spotlights:** Featured sections (tops, dresses, accessories) with sale items
- **Urgency Indicators:** "X sold in last hour" or "Low stock" badges
- **Social Proof Ticker:** Live purchase notifications ("Emma just bought this")
- **Sale Countdown Reminders:** Repeated timers mid-page and footer
- **Free Shipping Threshold:** Progress bar with suggested items
- **Bundle Suggestions:** "Shop the look" with sale items
- **Sale Terms:** Clear end time, exclusions if any, return policy
- **CTAs:** Hero, category sections, sticky "Shop Sale" bar (always visible)
- **FAQ:** 3-4 questions (When ends? Automatic discount? Returns? Exclusions?)

**Goal:** Maximize sales velocity during limited window by creating urgency through countdown timers and bestseller showcasing while ensuring frictionless shopping with automatic discount application and clear sale mechanics.`,
  },
  {
    icon: <Box />,
    title: "Fit Guarantee Landing Page",
    description:
      "Build a page explaining my fit guarantee with clear terms, customer success stories, and video walkthroughs of the return/exchange process.",
    prompt: `Build a fit guarantee landing page that eliminates purchase anxiety by showcasing transparent terms, real success stories, and frictionless return/exchange process.

**GUARANTEE TYPE:** e.g., "Perfect Fit Guarantee" OR "90-Day Fit Promise" OR "Free Returns & Exchanges Always"

**COVERAGE SCOPE:** e.g., Free returns, free exchanges, fit issues, sizing mistakes

**Audience Type:** Choose one:
- Fit-anxious (scared to buy without trying)
- Online shopping hesitant (burnt by bad returns)
- Between-size shoppers (need flexibility)

**Tone & Style:**
- Confident and reassuring
- Simple and transparent (no gotchas)
- Customer-first approach
- "Try risk-free" empowerment

**CTA Guidelines:** Each CTA should:
- Emphasize risk-free shopping ("Shop Without Worry")
- Use guarantee language ("Backed by Our Promise")
- Include ease messaging ("Easy Returns, Always")
- Reference fit confidence ("Find Your Perfect Fit")

**Conversion Tactics Use:**
- Real customer exchange stories
- Video walkthrough of return process
- Average processing time guarantees
- Free return shipping emphasis
- Multiple exchange allowances
- Size-up/down statistics ("80% nail it first try")

**Key Elements to Include:**
- **Hero Section:** Bold guarantee headline, "Shop Risk-Free" CTA
- **What's Covered:** Clear list (wrong size, fit issues, styling regrets, multiple exchanges)
- **How It Works:** 3-4 simple steps with visuals (Request → Ship back → Get new size)
- **Video Walkthrough:** 60-90 sec demo of return portal and process
- **Time Window:** 90 days, 60 days, or "anytime" clearly stated
- **Real Exchange Stories (4-6):** Customer name, what didn't fit, how handled, outcome, "now I love it" result
- **Success Metrics:** "12,000+ successful exchanges" or "Average 3-day turnaround"
- **No-Hassle Promise:** Free shipping both ways, no restocking fees, keep trying sizes
- **Return Portal Demo:** Screenshots showing how easy the process is
- **Size-Finding Stats:** "Most customers find perfect fit within 1-2 tries"
- **What Happens Next:** Timeline from return request to receiving new size
- **Customer Testimonials:** Quotes specifically about fit guarantee experience
- **Comparison:** Your guarantee vs. typical retail policies
- **Multi-Exchange Policy:** "Try as many sizes as you need"
- **CTAs:** Hero, after stories, after video, with products (5-6 total)
- **FAQ:** 6-8 questions (Free both ways? How many exchanges? International? Portal login? Processing time? Original tags?)

**Goal:** Convert fit-hesitant shoppers into confident buyers by providing radical transparency about your hassle-free fit guarantee, backed by real customer stories and visual process walkthroughs that prove trying sizes at home is truly risk-free.`,
  },
  {
    icon: <Percent />,
    title: "Fit Education Advertorial",
    description:
      "Create an educational advertorial teaching proper measurement techniques and fit terminology, with measurement guides and links to my products that solve fit problems.",
    prompt: `Build a fit education advertorial that teaches proper measuring and fit concepts while naturally positioning your products as solutions to common fit problems.

**TOPIC:** e.g., "The Measuring Guide Fashion Brands Don't Want You to Know" OR "Why Nothing Fits: A Sizing Education" OR "How to Actually Measure Yourself for Perfect Fit"

**PRODUCTS FEATURED:** e.g., "Our true-to-size collection" OR "Adjustable fit styles"

**Reader Type:** Choose one:
- Frustrated fitters (nothing ever fits right)
- Online shopping hesitant (scared of wrong size)
- Size-confused (don't know how to measure)

**Tone & Style:**
- Educational expert not condescending
- Empowering through knowledge
- Honest about industry sizing chaos
- Helpful problem-solver

**CTA Guidelines:** Each CTA should:
- Reference fit solution ("Shop True-to-Size")
- Use measurement language ("Find Your Exact Fit")
- Include sizing confidence ("Size Guide Included")
- Emphasize fit accuracy ("Consistent Sizing")

**Conversion Tactics Use:**
- Industry sizing inconsistency exposed
- Measurement tutorial with diagrams
- Fit terminology decoded
- Your brand's sizing consistency
- Comparison: vanity sizing vs. honest sizing
- Fit quiz or calculator tool

**Key Elements to Include:**
- **Hook:** Relatable fit frustration (2-3 paragraphs on sizing chaos)
- **The Sizing Problem:** Why nothing fits consistently across brands
- **How to Measure Yourself:** Step-by-step guide for 6-8 key measurements with diagrams
- **Measurement Tools Needed:** What to use (tape, mirror, helper)
- **Fit Terminology Explained:** Inseam, rise, shoulder width, bust vs. chest, etc.
- **Common Measuring Mistakes:** What people get wrong and how to fix
- **Body Shape Education:** Understanding proportions and what measurements mean
- **Size Chart Reading:** How to actually use size charts correctly
- **Your Brand's Approach:** Honest sizing philosophy and consistency
- **Fit Solutions Per Body Type:** Which styles work for different proportions
- **Product Callouts:** Items designed for fit problems (petite, tall, curvy fits)
- **Interactive Fit Tool:** Measurement calculator or size recommender
- **Real Customer Examples:** "I measured correctly and finally found my size"
- **Downloadable Measurement Guide:** PDF checklist for reference
- **CTAs:** After measuring guide, with fit solutions, conclusion (5-6 total)
- **FAQ:** 4-5 questions (Between sizes? Stretch fabric? Body changes? Alterations?)

**Goal:** Convert fit-frustrated shoppers into confident buyers by educating them on proper measuring techniques and fit concepts, then positioning your brand as the solution with honest, consistent sizing that actually works when measured correctly.`,
  },
];
